/**
 * PermissionMatrix - Two-state permission grid (Off / Full)
 * Matches the mockup design: grouped module cards, column headers,
 * slider toggle per permission, template default column, diff dot.
 *
 * READ is no longer selectable: access is granted or it isn't. Endpoints that
 * ask for `level: 'READ'` are still satisfied by FULL (FULL outranks READ), and
 * no role template grants READ, so dropping it from the editor costs nothing.
 * `AccessLevel` keeps the 'READ' member so a legacy value already stored on a
 * role still renders honestly rather than being silently shown as Off or Full.
 *
 * When `ceiling` is provided (derived-role edit mode):
 *  - permission key absent from ceiling  → row is locked OFF (disabled + lock icon)
 *  - ceiling value 'READ'               → locked; the only level we could offer is
 *                                          FULL, which would exceed the parent
 *  - ceiling value 'FULL'               → toggle cycles OFF ↔ FULL
 *  - A "Max allowed" column is shown per row
 */

import { useMemo } from 'react';
import styled from 'styled-components';
import { Shield, Lock } from 'lucide-react';
import {
  WORKFLOW_PERMISSION_PRESETS,
  SCOPED_RESOURCES,
  keysForLevel,
  keysForScope,
  type ScopedResource,
} from '@upskill/shared';

export type AccessLevel = 'READ' | 'FULL';

export interface PermissionDefinition {
  id: string;
  key: string;
  displayName: string;
  description?: string;
  resource: string;
  module?: string;
}

export interface PermissionValue {
  permissionId: string;
  accessLevel: AccessLevel;
}

interface PermissionMatrixProps {
  /** All available permissions */
  permissions: PermissionDefinition[];
  /** Current permission values (selected permissions with levels) */
  value: PermissionValue[];
  /** Called when permissions change */
  onChange: (value: PermissionValue[]) => void;
  /** Base template permissions for diff display */
  basePermissions?: PermissionValue[];
  /** Read-only mode */
  readOnly?: boolean;
  /**
   * Parent (system) role's max-granted levels, keyed by permission key (e.g. 'team:manage').
   *
   * CONTRACT with fe-senior: key = perm.key (string key), NOT perm.id.
   * Internally the component normalises to perm.id to keep a single key space.
   *
   * When provided (derived-role edit mode):
   *  - absent key  → parent OFF → row locked (disabled + lock icon, cannot be enabled)
   *  - 'READ'      → toggle may only cycle OFF ↔ READ (FULL is blocked)
   *  - 'FULL'      → full cycle OFF / READ / FULL
   *
   * When undefined (e.g. super-admin editing a system role) → behaviour unchanged.
   */
  ceiling?: Record<string, AccessLevel>;
}

// ─── Pure utilities (exported for use in RoleDetailPage / EditRoleModal) ────

const groupByResource = (permissions: PermissionDefinition[]) => {
  const groups: Record<string, PermissionDefinition[]> = {};
  for (const perm of permissions) {
    const resource = perm.resource || 'Other';
    if (!groups[resource]) groups[resource] = [];
    groups[resource].push(perm);
  }
  return Object.fromEntries(
    Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)),
  );
};

// ─── Grouped rows (one control over several permission keys) ────────────────

/**
 * Some permissions are not independently meaningful — they are levels of one
 * setting spread across several keys, and the API only accepts certain
 * combinations. Those render as ONE row with a segmented control instead of one
 * Off/Full toggle per key, which makes the invalid combinations unrepresentable:
 *
 *  - Scoped resources (`team:manage` + `team:manage:all`): every endpoint is
 *    gated on the BASE key and only consults `:all` afterwards to widen scope,
 *    so "All without Own" is a 403, not a broader grant.
 *  - Workflows (`red_dot:execute` + `red_dot:manage`): Manage is defined as a
 *    superset of Execute.
 *
 * Both key sets come from `@upskill/shared/constants/rbac` — the same definitions
 * the org-admin role editor and the API use — so a level means the same thing
 * everywhere and a new workflow only has to be declared once.
 */
interface GroupedOption {
  value: string;
  label: string;
  /** Keys to grant when this option is selected (empty = revoke the row). */
  keys: string[];
  title: string;
}

interface GroupedRow {
  label: string;
  description?: string;
  /** Every key this row owns; none of them render a row of their own. */
  memberKeys: string[];
  /** Ordered lowest → highest. The first entry is always the "off" option. */
  options: GroupedOption[];
}

/** Keyed by the permission key whose row the group replaces. */
const GROUPED_ROWS: Record<string, GroupedRow> = {};

for (const [resource, { base, all }] of Object.entries(SCOPED_RESOURCES) as [
  ScopedResource,
  { base: string; all: string },
][]) {
  GROUPED_ROWS[base] = {
    label: '', // filled from the base permission's displayName at render time
    memberKeys: [base, all],
    options: [
      { value: 'OFF', label: 'Off', keys: [], title: 'No access' },
      {
        value: 'OWN',
        label: 'Own',
        keys: keysForScope(resource, 'own'),
        title: 'Their own records and everyone beneath them',
      },
      {
        value: 'ALL',
        label: 'All',
        keys: keysForScope(resource, 'all'),
        title: 'Every record in the organization',
      },
    ],
  };
}

/** Display name per workflow code — the permission keys are named per-action. */
const WORKFLOW_ROW_LABELS: Record<string, string> = {
  red_dot: 'Red Dot',
  preference_assessment: 'Preference Assessment',
};

for (const [code, preset] of Object.entries(WORKFLOW_PERMISSION_PRESETS)) {
  const memberKeys = [...new Set([...preset.execute, ...preset.manage])];
  // The row replaces the workflow's execute key (its lowest-level key).
  const primaryKey = preset.execute[0];
  if (!primaryKey) continue;
  GROUPED_ROWS[primaryKey] = {
    label: WORKFLOW_ROW_LABELS[code] ?? code,
    memberKeys,
    options: [
      { value: 'OFF', label: 'Off', keys: [], title: 'No access' },
      {
        value: 'EXECUTE',
        label: 'Execute',
        keys: keysForLevel(code, 'execute'),
        title: 'Create, fill and submit',
      },
      {
        value: 'MANAGE',
        label: 'Manage',
        keys: keysForLevel(code, 'manage'),
        title: 'Execute, plus advance / reassign / administer',
      },
    ],
  };
}

/**
 * Which option a set of granted keys represents. Walks HIGHEST → lowest and
 * picks the first option that has a key the option below it does not.
 *
 * Deliberately prefers the higher level for a partial/legacy set. A role holding
 * `red_dot:manage` without `red_dot:execute` shows as Manage, not Execute:
 * manage endpoints are gated on `:manage` alone, so rendering it lower and
 * saving would silently revoke access the role really has. Selecting the level
 * again writes the canonical key set, repairing the row.
 */
export const optionForKeys = (row: GroupedRow, grantedKeys: Set<string>): string => {
  for (let i = row.options.length - 1; i > 0; i--) {
    const lower = new Set(row.options[i - 1].keys);
    const exclusive = row.options[i].keys.filter((k) => !lower.has(k));
    if (exclusive.some((k) => grantedKeys.has(k))) return row.options[i].value;
  }
  // Nothing exclusive matched — any member key at all means the lowest grant.
  if (row.memberKeys.some((k) => grantedKeys.has(k))) return row.options[1].value;
  return row.options[0].value;
};

/**
 * The two states of an ordinary (ungrouped) permission. Same segmented control
 * as the grouped rows, so every row on the screen is operated the same way.
 */
const LEVEL_OPTIONS: { value: AccessLevel | null; label: string }[] = [
  { value: null, label: 'Off' },
  { value: 'FULL', label: 'Full' },
];

const getGridTemplate = (hasCeiling: boolean, hasBase: boolean): string => {
  if (hasCeiling && hasBase) return '5fr 3fr 2fr 3fr 1fr';
  if (hasCeiling) return '5fr 3fr 2fr';
  if (hasBase) return '5fr 3fr 3fr 1fr';
  return '5fr 3fr';
};

// ─── Component ──────────────────────────────────────────────────────────────

export const PermissionMatrix = ({
  permissions,
  value,
  onChange,
  basePermissions,
  readOnly = false,
  ceiling,
}: PermissionMatrixProps) => {
  const grouped = useMemo(() => groupByResource(permissions), [permissions]);

  const valueMap = useMemo(() => {
    const map = new Map<string, AccessLevel>();
    for (const v of value) map.set(v.permissionId, v.accessLevel);
    return map;
  }, [value]);

  const baseMap = useMemo(() => {
    if (!basePermissions) return null;
    const map = new Map<string, AccessLevel>();
    for (const v of basePermissions) map.set(v.permissionId, v.accessLevel);
    return map;
  }, [basePermissions]);

  // Normalise ceiling from perm.key → perm.id so the whole component uses one key space.
  const ceilingById = useMemo(() => {
    if (!ceiling) return null;
    const keyToId = new Map<string, string>();
    for (const perm of permissions) keyToId.set(perm.key, perm.id);
    const map = new Map<string, AccessLevel>();
    for (const [key, level] of Object.entries(ceiling)) {
      const id = keyToId.get(key);
      if (id) map.set(id, level);
    }
    return map;
  }, [ceiling, permissions]);

  /**
   * Grouped rows resolved against the permissions actually present, keyed by the
   * perm.id of the row they replace. Members other than that primary key are
   * folded away and never render a row of their own.
   */
  const groupedRows = useMemo(() => {
    const byKey = new Map(permissions.map((p) => [p.key, p]));
    const rows = new Map<
      string,
      { def: GroupedRow; label: string; description?: string; memberIds: string[] }
    >();
    const foldedIds = new Set<string>();

    for (const [primaryKey, def] of Object.entries(GROUPED_ROWS)) {
      const primary = byKey.get(primaryKey);
      if (!primary) continue;
      const members = def.memberKeys
        .map((k) => byKey.get(k))
        .filter((p): p is PermissionDefinition => p !== undefined);
      // Only group when more than the primary key is present; otherwise the row
      // has nothing to merge and behaves as an ordinary permission.
      if (members.length < 2) continue;

      rows.set(primary.id, {
        def,
        // Scoped resources keep the permission's own name ("Manage Team");
        // workflows use an explicit label, since their keys are named per-action.
        label: def.label || primary.displayName,
        description: def.label ? undefined : primary.description,
        memberIds: members.map((m) => m.id),
      });
      for (const m of members) if (m.id !== primary.id) foldedIds.add(m.id);
    }
    return { rows, foldedIds };
  }, [permissions]);

  /** perm.key for a given perm.id — grouped rows reason in key space. */
  const keyById = useMemo(
    () => new Map(permissions.map((p) => [p.id, p.key])),
    [permissions],
  );

  const grantedKeysOf = (levels: Map<string, AccessLevel>): Set<string> => {
    const keys = new Set<string>();
    for (const id of levels.keys()) {
      const key = keyById.get(id);
      if (key) keys.add(key);
    }
    return keys;
  };

  const changedCount = useMemo(() => {
    if (!baseMap) return 0;
    // A grouped row is ONE control, so moving Off → Manage counts as one change
    // even though it writes two keys.
    const groupedMemberIds = new Set<string>();
    for (const [primaryId, row] of groupedRows.rows) {
      groupedMemberIds.add(primaryId);
      for (const id of row.memberIds) groupedMemberIds.add(id);
    }
    let count = 0;
    for (const [id, baseLevel] of baseMap) {
      if (groupedMemberIds.has(id)) continue;
      const currentLevel = valueMap.get(id);
      if (!currentLevel || currentLevel !== baseLevel) count++;
    }
    for (const id of valueMap.keys()) {
      if (groupedMemberIds.has(id)) continue;
      if (!baseMap.has(id)) count++;
    }
    const currentKeys = grantedKeysOf(valueMap);
    const templateKeys = grantedKeysOf(baseMap);
    for (const row of groupedRows.rows.values()) {
      if (
        optionForKeys(row.def, currentKeys) !== optionForKeys(row.def, templateKeys)
      ) {
        count++;
      }
    }
    return count;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueMap, baseMap, groupedRows, keyById]);

  const hasCeiling = ceilingById !== null;
  const hasBase = baseMap !== null;

  const setPermissionLevel = (permId: string, level: AccessLevel | null) => {
    if (readOnly) return;
    if (level === null) {
      onChange(value.filter((v) => v.permissionId !== permId));
    } else {
      const existing = valueMap.get(permId);
      if (!existing) {
        onChange([...value, { permissionId: permId, accessLevel: level }]);
      } else {
        onChange(
          value.map((v) =>
            v.permissionId === permId ? { ...v, accessLevel: level } : v,
          ),
        );
      }
    }
  };

  /**
   * Write a grouped row in ONE onChange so its keys can never be persisted out
   * of step (e.g. `:all` without its base key, which the API would reject).
   */
  const setGroupedOption = (
    memberIds: string[],
    row: GroupedRow,
    optionValue: string,
  ) => {
    if (readOnly) return;
    const memberIdSet = new Set(memberIds);
    const without = value.filter((v) => !memberIdSet.has(v.permissionId));
    const option = row.options.find((o) => o.value === optionValue);
    if (!option || option.keys.length === 0) {
      onChange(without);
      return;
    }
    const idByKey = new Map(
      memberIds
        .map((id) => [keyById.get(id), id] as const)
        .filter((e): e is [string, string] => e[0] !== undefined),
    );
    const granted: PermissionValue[] = option.keys
      .map((k) => idByKey.get(k))
      .filter((id): id is string => id !== undefined)
      .map((id) => ({ permissionId: id, accessLevel: 'FULL' as AccessLevel }));
    onChange([...without, ...granted]);
  };

  return (
    <Container>
      {Object.entries(grouped).map(([resource, perms]) => (
        <ModuleCard key={resource}>
          {/* Resource header */}
          <ModuleHeader>
            <Shield size={13} />
            <ModuleLabel>{resource}</ModuleLabel>
          </ModuleHeader>

          {/* Column headers */}
          <ColumnHeaders $hasCeiling={hasCeiling} $hasBase={hasBase}>
            <ColPermission>Permission</ColPermission>
            <ColAccess>Access</ColAccess>
            {hasCeiling && <ColCeiling>Max allowed</ColCeiling>}
            {hasBase && <ColTemplate>Template Default</ColTemplate>}
            {hasBase && <ColDiff>Diff</ColDiff>}
          </ColumnHeaders>

          {/* Permission rows */}
          <PermissionList>
            {perms.map((perm) => {
              // Folded members (`:all`, `:manage`) live inside their group's row.
              if (groupedRows.foldedIds.has(perm.id)) return null;

              const group = groupedRows.rows.get(perm.id);
              const isGrouped = group !== undefined;

              const level = valueMap.get(perm.id) ?? null;
              const baseLevel = baseMap?.get(perm.id) ?? null;
              const cap = ceilingById?.get(perm.id); // undefined | 'READ' | 'FULL'
              // A READ-capped parent is locked too: FULL is the only level the
              // control can now produce, and that would exceed the parent's grant.
              const locked = hasCeiling && (cap === undefined || cap === 'READ');
              const effectiveReadOnly = readOnly || locked;

              // ── Grouped row (Off / Own / All, or Off / Execute / Manage) ──
              const currentOption = isGrouped
                ? optionForKeys(group.def, grantedKeysOf(valueMap))
                : null;
              const templateOption =
                isGrouped && baseMap
                  ? optionForKeys(group.def, grantedKeysOf(baseMap))
                  : null;
              /** An option is offerable only if the parent grants every key it writes. */
              const optionAllowed = (opt: GroupedOption) => {
                if (!hasCeiling) return true;
                const idByKey = new Map(
                  group!.memberIds
                    .map((id) => [keyById.get(id), id] as const)
                    .filter((e): e is [string, string] => e[0] !== undefined),
                );
                return opt.keys.every((k) => {
                  const id = idByKey.get(k);
                  return id !== undefined && ceilingById?.get(id) !== undefined;
                });
              };

              const isDifferent = isGrouped
                ? hasBase && currentOption !== templateOption
                : hasBase &&
                  ((baseLevel !== null && level !== baseLevel) ||
                    (baseLevel !== null && level === null) ||
                    (baseLevel === null && level !== null));

              return (
                <PermissionRow
                  key={perm.id}
                  $readOnly={effectiveReadOnly}
                  $hasBase={hasBase}
                  $hasCeiling={hasCeiling}
                  $locked={locked}
                >
                  {/* Permission label */}
                  <CellPermission>
                    <PermissionName $locked={locked}>
                      {isGrouped ? group.label : perm.displayName}
                    </PermissionName>
                    {(isGrouped ? group.description : perm.description) && (
                      <PermissionDesc>
                        {isGrouped ? group.description : perm.description}
                      </PermissionDesc>
                    )}
                  </CellPermission>

                  {/* Off/Own/All group (scoped pair), toggle, or locked indicator */}
                  <CellAccess>
                    {locked ? (
                      <LockedOffCell
                        aria-label="Locked — parent role does not grant this permission"
                      >
                        <Lock size={13} />
                        <LockedLabel>Locked</LockedLabel>
                      </LockedOffCell>
                    ) : isGrouped ? (
                      <ScopeGroup
                        role="group"
                        aria-label={`${group.label} access level`}
                      >
                        {group.def.options.map((opt) => {
                          const allowed = optionAllowed(opt);
                          const disabled = effectiveReadOnly || !allowed;
                          return (
                            <ScopeButton
                              key={opt.value}
                              type="button"
                              $active={currentOption === opt.value}
                              $disabled={disabled}
                              disabled={disabled}
                              aria-pressed={currentOption === opt.value}
                              title={
                                allowed
                                  ? opt.title
                                  : 'The parent role does not grant this level'
                              }
                              onClick={() => {
                                if (disabled) return;
                                setGroupedOption(group.memberIds, group.def, opt.value);
                              }}
                            >
                              {opt.label}
                            </ScopeButton>
                          );
                        })}
                      </ScopeGroup>
                    ) : (
                      <>
                        <ScopeGroup
                          role="group"
                          aria-label={`${perm.displayName} access`}
                        >
                          {LEVEL_OPTIONS.map((opt) => {
                            const active = level === opt.value;
                            return (
                              <ScopeButton
                                key={opt.label}
                                type="button"
                                $active={active}
                                $disabled={effectiveReadOnly}
                                disabled={effectiveReadOnly}
                                aria-pressed={active}
                                title={opt.value === 'FULL' ? 'Full access' : 'No access'}
                                onClick={() => {
                                  if (effectiveReadOnly) return;
                                  setPermissionLevel(perm.id, opt.value);
                                }}
                              >
                                {opt.label}
                              </ScopeButton>
                            );
                          })}
                        </ScopeGroup>
                        {/* Legacy READ can no longer be set, but must not be
                            misreported as Off or Full while it is still stored. */}
                        {level === 'READ' && (
                          <LegacyHint title="Read-only was removed; pick Off or Full to replace it">
                            Read
                          </LegacyHint>
                        )}
                      </>
                    )}
                  </CellAccess>

                  {/* Max allowed (ceiling) */}
                  {hasCeiling && (
                    <CellCeiling>
                      <CeilingBadge $locked={locked} $cap={cap}>
                        {locked
                          ? '—'
                          : isGrouped
                            ? // Highest level the parent role allows.
                              ([...group.def.options]
                                .reverse()
                                .find((o) => optionAllowed(o))?.label ?? '—')
                            : cap === 'FULL'
                              ? 'Full'
                              : 'View'}
                      </CeilingBadge>
                    </CellCeiling>
                  )}

                  {/* Template default */}
                  {hasBase && (
                    <CellTemplate>
                      <TemplateBadge
                        $level={
                          isGrouped
                            ? templateOption === group.def.options[0].value
                              ? null
                              : 'FULL'
                            : baseLevel
                        }
                      >
                        {isGrouped
                          ? (group.def.options.find((o) => o.value === templateOption)
                              ?.label ?? 'Off')
                          : baseLevel === 'FULL'
                            ? 'Full'
                            : baseLevel === 'READ'
                              ? 'Read'
                              : 'Off'}
                      </TemplateBadge>
                    </CellTemplate>
                  )}

                  {/* Diff indicator */}
                  {hasBase && (
                    <CellDiff>
                      {isDifferent && <DiffDot title="Changed from template" />}
                    </CellDiff>
                  )}
                </PermissionRow>
              );
            })}
          </PermissionList>
        </ModuleCard>
      ))}

      {hasBase && changedCount > 0 && (
        <Footer>
          {changedCount} permission{changedCount !== 1 ? 's' : ''} changed from template
        </Footer>
      )}

      {hasCeiling && (
        <CeilingLegend>
          <Lock size={11} />
          Locked rows are not granted by the parent system role and cannot be enabled.
        </CeilingLegend>
      )}
    </Container>
  );
};

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const ModuleCard = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.default};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

const ModuleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: 10px ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};

  svg {
    color: ${({ theme }) => theme.colors.text.light};
    flex-shrink: 0;
  }
`;

const ModuleLabel = styled.h3`
  flex: 1;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
`;

const ColumnHeaders = styled.div<{ $hasCeiling: boolean; $hasBase: boolean }>`
  display: grid;
  grid-template-columns: ${({ $hasCeiling, $hasBase }) =>
    getGridTemplate($hasCeiling, $hasBase)};
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.light};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const ColPermission = styled.div``;
const ColAccess = styled.div`
  text-align: center;
`;
const ColCeiling = styled.div`
  text-align: center;
`;
const ColTemplate = styled.div`
  text-align: center;
`;
const ColDiff = styled.div`
  text-align: center;
`;

const PermissionList = styled.div`
  display: flex;
  flex-direction: column;
`;

const PermissionRow = styled.div<{
  $readOnly: boolean;
  $hasBase: boolean;
  $hasCeiling: boolean;
  $locked: boolean;
}>`
  display: grid;
  grid-template-columns: ${({ $hasCeiling, $hasBase }) =>
    getGridTemplate($hasCeiling, $hasBase)};
  gap: ${({ theme }) => theme.spacing[2]};
  align-items: center;
  padding: 10px ${({ theme }) => theme.spacing[4]};
  transition: background 0.15s;

  ${({ $locked, theme }) =>
    $locked &&
    `background: ${theme.colors.status.locked.bg}30;`}

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light}20;
  }

  &:hover {
    background: ${({ theme, $readOnly, $locked }) =>
      $locked
        ? `${theme.colors.status.locked.bg}40`
        : $readOnly
          ? 'transparent'
          : `${theme.colors.backgrounds.gray}80`};
  }
`;

const CellPermission = styled.div`
  min-width: 0;
`;

const PermissionName = styled.div<{ $locked: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ $locked, theme }) =>
    $locked ? theme.colors.text.light : theme.colors.text.primary};
`;

const PermissionDesc = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
  margin-top: 1px;
`;

const CellAccess = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const LockedOffCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => theme.colors.status.locked.text};
`;

const LockedLabel = styled.span`
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

/* 3-position slider toggle */
/**
 * Segmented Off / Own / All control for a scoped permission pair. Sized to sit
 * in the same access column as the two-state toggle, so rows stay aligned.
 */
const ScopeGroup = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid ${({ theme }) => theme.colors.borders.default};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  /* Fill the access column up to a comfortable cap, then stop growing. The
     three segments share the width and shrink together on narrower columns
     (the grid gives this cell only 3fr once the ceiling/template columns are
     present), so the row never forces page-level overflow on a tablet. */
  width: 100%;
  max-width: 210px;
`;

const ScopeButton = styled.button<{ $active: boolean; $disabled: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  padding: 7px 6px;
  height: 32px;
  border: none;
  border-right: 1px solid ${({ theme }) => theme.colors.borders.default};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.15s, color 0.15s;

  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary.base : theme.colors.backgrounds.white};
  color: ${({ $active, $disabled, theme }) => {
    if ($active) return theme.colors.backgrounds.white;
    if ($disabled) return theme.colors.text.light;
    return theme.colors.text.secondary;
  }};
  opacity: ${({ $disabled, $active }) => ($disabled && !$active ? 0.5 : 1)};

  &:last-child {
    border-right: none;
  }

  &:hover:not(:disabled) {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.primary.base : theme.colors.primary.lightSky};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: -2px;
  }
`;

/** Marker for a stored READ level, which the editor can no longer produce. */
const LegacyHint = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.status.warning.text};
  white-space: nowrap;
`;

const CellCeiling = styled.div`
  display: flex;
  justify-content: center;
`;

const CeilingBadge = styled.span<{ $locked: boolean; $cap: AccessLevel | undefined }>`
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};

  background: ${({ $locked, $cap, theme }) => {
    if ($locked) return theme.colors.status.locked.bg;
    if ($cap === 'FULL') return `${theme.colors.primary.lightSky}40`;
    return theme.colors.status.warning.bg;
  }};

  color: ${({ $locked, $cap, theme }) => {
    if ($locked) return theme.colors.status.locked.text;
    if ($cap === 'FULL') return theme.colors.primary.base;
    return theme.colors.status.warning.text;
  }};
`;

const CellTemplate = styled.div`
  display: flex;
  justify-content: center;
`;

const TemplateBadge = styled.span<{ $level: AccessLevel | null }>`
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};

  background: ${({ $level, theme }) => {
    if ($level === 'FULL') return `${theme.colors.primary.lightSky}40`;
    if ($level === 'READ') return theme.colors.status.warning.bg;
    return theme.colors.backgrounds.gray;
  }};

  color: ${({ $level, theme }) => {
    if ($level === 'FULL') return theme.colors.primary.base;
    if ($level === 'READ') return theme.colors.status.warning.text;
    return theme.colors.text.light;
  }};
`;

const CellDiff = styled.div`
  display: flex;
  justify-content: center;
`;

const DiffDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status.warning.text};
  flex-shrink: 0;
`;

const Footer = styled.div`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.status.warning.text};
  background: ${({ theme }) => theme.colors.status.warning.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-align: center;
`;

const CeilingLegend = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.status.locked.text};
  background: ${({ theme }) => theme.colors.status.locked.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.locked.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};

  svg {
    flex-shrink: 0;
  }
`;

export default PermissionMatrix;
